import React, { memo, useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { isRTL, rtlWritingDirection } from '../../shared/i18n/rtl';
import { appTheme, useThemeColors, type ThemeColors } from '../../shared/theme';

export interface MenuFlowCounterProps {
  label: string;
  count: number;
  selectedCount: number;
  isActive?: boolean;
  /** Strip chrome when embedded in the merged counters bar. */
  bare?: boolean;
}

function createMenuFlowCounterStyles(c: ThemeColors) {
  const PILL_RADIUS = 18;
  const rtl = isRTL();
  return StyleSheet.create({
    counterItem: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 6,
      paddingHorizontal: 10,
      borderRadius: PILL_RADIUS,
      backgroundColor: c.surface,
      minWidth: 56,
      height: 30,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: c.border,
    },
    counterItemBare: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 0,
      paddingHorizontal: 2,
      minWidth: 0,
      height: 24,
      backgroundColor: 'transparent',
      borderWidth: 0,
    },
    counterLabel: {
      fontSize: 11,
      fontWeight: '600',
      color: c.textSecondary,
      marginEnd: 6,
      textTransform: rtl ? 'none' : 'uppercase',
      letterSpacing: rtl ? 0 : 0.4,
      writingDirection: rtlWritingDirection(),
    },
    counterLabelActive: {
      color: c.text,
    },
    counterBoxRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 3,
    },
    counterBox: {
      width: 6,
      height: 6,
      borderRadius: 3,
      borderWidth: 1,
      borderColor: c.border,
      backgroundColor: c.surfaceElevated,
    },
    counterBoxFilled: {
      borderColor: c.primary,
      backgroundColor: c.primary,
    },
  });
}

const MenuFlowCounterComponent: React.FC<MenuFlowCounterProps> = ({
  label,
  count,
  selectedCount,
  isActive = false,
  bare = false,
}) => {
  const colors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createMenuFlowCounterStyles(colors), [colors]);

  const boxes = useMemo(() => {
    return Array.from({ length: count }, (_, idx) => {
      const isFilled = idx < selectedCount;
      return (
        <View
          key={`${label}-${idx}`}
          style={[styles.counterBox, isFilled && styles.counterBoxFilled]}
        />
      );
    });
  }, [count, selectedCount, label, styles]);

  return (
    <View style={bare ? styles.counterItemBare : styles.counterItem}>
      <Text style={[styles.counterLabel, isActive && styles.counterLabelActive]} numberOfLines={1}>
        {label}
      </Text>
      <View style={styles.counterBoxRow}>{boxes}</View>
    </View>
  );
};

export const MenuFlowCounter = memo(MenuFlowCounterComponent);
